import { client } from '@/shared/lib/client';

/**
 * 计费和套餐服务API接口
 */

// API响应类型定义
export interface ApiResponse<T = any> {
  status: number;
  message: string;
  data: T;
}

// 套餐项类型定义
export interface PlanItem {
  id: number;
  planCode: string;
  skuId: string; // SKU ID
  productCategory?: string; // 产品类别
  productType?: string; // 产品类型
  planName: string;
  planKind: number; // 套餐类型
  periodDays: number;
  periodType?: string; // 周期类型（英文）如 "Month", "Year"
  periodTypeCn?: string; // 周期类型（中文）如 "月", "年"
  creditPoints?: number; // 积分额度
  creditTotal?: number; // 总额度（兼容旧字段）
  creditUnit: string;
  currencyUnit: string;
  recommended: number; // 是否推荐 0=否 1=是
  featureList: string | string[]; // 特性列表（英文），可为JSON字符串或数组
  featureListCn: string | string[]; // 特性列表（中文），可为JSON字符串或数组
  chargePrice: number;
  actualPrice: number;
  discountType: string;
  discountRate: number;
  planType?: string; // 套餐类型名称（英文），如 "Monthly Plan"
  planTypeCn?: string; // 套餐类型名称（中文），如 "单月计划"
  amountUnit?: string; // 数量单位（英文），如 "Slot"
  amountUnitCn?: string; // 数量单位（中文），如 "席位"
  amount?: number;
  autoRenew: number;
  status: number;
  createdBy?: string; // 创建人
  createdAt: number;
  updatedAt: number;
  purchased?: boolean; // 是否已购买
  purchaseAllowed?: boolean; // 是否允许购买
}

// 活跃订阅项类型定义
export interface ActivePlanItem {
  subscriptionId: number; // 订阅ID
  userId: string; // 用户ID
  streamlakeUserId?: string; // StreamLake用户ID
  subscriptionKind: number; // 订阅类型
  creditTotal: number; // 总额度
  creditUsed: number; // 已用额度
  creditAvailable: number; // 可用额度
  creditUnit: string; // 额度单位
  effectiveStart: number; // 生效开始时间(毫秒时间戳)
  effectiveEnd: number; // 生效结束时间(毫秒时间戳)
  autoRenew: number; // 是否自动续费(0=否,1=是)
  subscriptionStatus: number; // 订阅状态
  paymentOrderNo: string; // 支付订单号
  planCode: string; // 套餐代码
  planName: string; // 套餐名称
  chargePrice: number; // 订单金额(分)
  actualPrice: number; // 实付金额(分)
  currency: string; // 货币类型
  payMethod: string; // 支付方式
  isFirstOrder: number; // 是否首单(0=否,1=是)
  createdAt: number; // 订单创建时间(毫秒时间戳)
  paymentOrderStatus: string; // 支付订单状态
}

// 套餐列表请求参数
export interface PlanListRequest {
  page?: number; // 页码，默认1
  pageSize?: number; // 每页大小，默认20
  userId?: string; // 用户ID，内部域名时必传
}

// 套餐列表响应
export interface PlanListResponse {
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  planList: PlanItem[];
}

// 活跃订阅列表响应
export interface ActivePlansResponse extends ApiResponse<ActivePlanItem[]> {
  host?: string;
  port?: number;
  timestamp?: number;
}

/**
 * 获取套餐列表
 * @param params 请求参数
 * @returns Promise<ApiResponse<PlanListResponse>>
 */
export const getPlanList = async (
  params?: PlanListRequest,
): Promise<ApiResponse<PlanListResponse>> => {
  try {
    // 根据域名类型添加对应的前缀
    const url = '/api/proxy/api/v1/billing/plan/list';

    const response = (await client({
      url,
      method: 'GET',
      params: {
        page: params?.page || 1,
        pageSize: params?.pageSize || 20,
        userId: params?.userId,
      },
    })) as ApiResponse<PlanListResponse>;
    return response;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to get plan list');
  }
};

/**
 * 获取用户当前活跃订阅列表
 * @returns Promise<ActivePlansResponse>
 */
export const getActivePlans = async (): Promise<ActivePlansResponse> => {
  try {
    const url = '/api/proxy/api/v1/billing/activePlans';

    const response = (await client({
      url,
      method: 'GET',
    })) as ActivePlansResponse;
    return response;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to get active plans');
  }
};

// 产品详情类型定义
export interface ProductDetail {
  skuId: string; // SKU ID
  productCategory: string; // 产品类别
  productType: string; // 产品类型
  spec: string; // 规格（如 monthly）
  skuName: string; // SKU名称
  price: number; // 价格
  originPrice: number; // 原价
  finalPrice: number; // 最终价格
  currencyUnit: string; // 货币单位
  productAmount: string; // 产品数量
  productUnit: string; // 产品单位（如 month）
  spuId: string; // SPU ID
  count: number; // 数量
  subEnabled: boolean; // 是否启用订阅
  countUnit: string; // 数量单位
}

// 预下单详情类型定义（新版API）
export interface PreOrderDetail {
  code: number; // 业务状态码
  msg: string; // 业务消息
  orderOutNo: string; // 订单号
  orderTotal: number; // 订单总价
  orderOriginTotal: number; // 原价总价
  remainingDays: number; // 剩余天数
  remainingMonths: number; // 剩余月数
  daysOfMonth: number; // 当月天数
  productDetailList: ProductDetail[]; // 产品详情列表
  plan?: PlanItem; // 套餐信息
}

// 预下单请求参数
export interface PreOrderRequest {
  skuId: string; // SKU ID（套餐标识）
}

/**
 * 预下单接口 - 获取订单详情
 * @param params 请求参数
 * @returns Promise<ApiResponse<PreOrderDetail>>
 */
export const preOrder = async (params: PreOrderRequest): Promise<ApiResponse<PreOrderDetail>> => {
  try {
    const url = '/api/proxy/api/v1/billing/streamlake/preOrder';

    const response = (await client({
      url,
      method: 'GET',
      params,
    })) as ApiResponse<PreOrderDetail>;
    return response;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to create pre-order');
  }
};

// 支付订单数据类型定义
export interface SubmitOrderData {
  code: number; // 业务状态码
  msg: string; // 业务消息
  orderOutNo: string;
  passToken: string;
  sid: string;
  returnUrl: string;
  userId: string;
}

// 支付订单请求参数
export interface SubmitOrderRequest {
  orderOutNo: string; // 订单号（预下单返回的订单号）
  skuId: string; // SKU ID（套餐标识）
  returnUrl: string; // 支付完成后跳转的页面URL
  deviceId: string; // 浏览器设备ID
  subEnabled?: boolean; // 是否启用订阅（默认true）
  productDetailList: ProductDetail[]; // 产品详情列表（可选，用于更新订单）
}

/**
 * 生成设备ID
 * @returns 设备ID字符串
 */
export const generateDeviceId = (): string => {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
};

// 白名单检查响应
export interface CheckWhiteListData {
  whiteListEnabled: boolean;
  showPricing: boolean;
}

/**
 * 检查用户是否命中白名单，决定是否显示价格页
 * @returns Promise<ApiResponse<CheckWhiteListData>>
 */
export const checkWhiteList = async (userId?: string): Promise<ApiResponse<CheckWhiteListData>> => {
  try {
    const url = '/api/proxy/api/v1/billing/plan/checkWhiteList';

    const response = (await client({
      url,
      method: 'GET',
      params: { userId },
    })) as ApiResponse<CheckWhiteListData>;
    return response;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to check whitelist');
  }
};

/**
 * 提交订单接口 - 实现支付
 * @param params 请求参数
 * @returns Promise<ApiResponse<SubmitOrderData>>
 */
export const submitOrder = async (
  params: SubmitOrderRequest,
): Promise<ApiResponse<SubmitOrderData>> => {
  try {
    const url = '/api/proxy/api/v1/billing/streamlake/submitOrder';

    const response = (await client({
      url,
      method: 'POST',
      data: params,
    })) as ApiResponse<SubmitOrderData>;
    return response;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to submit order');
  }
};
